import { createSHA256 } from 'hash-wasm'
import { gatewayRequest } from '../api/gateway.js'
import { readStore, writeStore } from '../storage/db.js'
import { ASSET_URI_RE, scanAssetUris } from './document.js'

const SMALL_UPLOAD_LIMIT = 100 * 1024 * 1024
const PART_SIZE = 16 * 1024 * 1024
const URL_REFRESH_SKEW = 60_000
const VIDEO_EXTENSIONS = new Set(['mp4', 'webm', 'mov'])

async function createVideoPoster(source, timeoutMs = 5_000) {
  if (typeof document === 'undefined') return null
  const video = document.createElement('video')
  video.preload = 'auto'
  video.muted = true
  video.playsInline = true
  if (/^https:/i.test(source)) video.crossOrigin = 'anonymous'
  let timer = null
  const waitFor = (event, errorEvent = 'error') => new Promise((resolve, reject) => {
    const cleanup = () => {
      video.removeEventListener(event, onReady)
      video.removeEventListener(errorEvent, onError)
      if (timer != null) clearTimeout(timer)
    }
    const onReady = () => { cleanup(); resolve() }
    const onError = () => { cleanup(); reject(new Error('video_poster_unavailable')) }
    video.addEventListener(event, onReady, { once: true })
    video.addEventListener(errorEvent, onError, { once: true })
    timer = setTimeout(onError, timeoutMs)
  })
  try {
    const loaded = waitFor('loadeddata')
    video.src = source
    await loaded
    const duration = Number.isFinite(video.duration) ? video.duration : 0
    const target = Math.min(0.1, Math.max(0, duration - 0.05))
    if (target > 0 && Math.abs(video.currentTime - target) > 0.001) {
      const sought = waitFor('seeked')
      video.currentTime = target
      await sought
    }
    const naturalW = video.videoWidth || 0
    const naturalH = video.videoHeight || 0
    if (!naturalW || !naturalH) return null
    const scale = Math.min(1, 1500 / Math.max(naturalW, naturalH))
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(naturalW * scale))
    canvas.height = Math.max(1, Math.round(naturalH * scale))
    const context = canvas.getContext('2d', { alpha: false })
    if (!context) return null
    context.drawImage(video, 0, 0, canvas.width, canvas.height)
    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.86))
    if (!blob) return null
    return { url: URL.createObjectURL(blob), duration, width: naturalW, height: naturalH }
  } catch {
    return null
  } finally {
    if (timer != null) clearTimeout(timer)
    video.removeAttribute('src')
    try { video.load() } catch {}
  }
}

export function extensionFor(file) {
  const nameExt = String(file.name || '').match(/\.([A-Za-z0-9]{1,8})$/)?.[1]?.toLowerCase()
  if (nameExt) return nameExt === 'jpeg' ? 'jpg' : nameExt
  return ({ 'image/png': 'png', 'image/jpeg': 'jpg', 'image/webp': 'webp', 'image/gif': 'gif', 'image/svg+xml': 'svg', 'video/mp4': 'mp4', 'video/webm': 'webm', 'model/gltf-binary': 'glb' })[file.type] || 'bin'
}

async function hashFileFallback(file) {
  const sha = await createSHA256()
  sha.init()
  for (let offset = 0; offset < file.size; offset += 4 * 1024 * 1024) {
    sha.update(new Uint8Array(await file.slice(offset, offset + 4 * 1024 * 1024).arrayBuffer()))
  }
  return sha.digest('hex').slice(0, 16)
}

export async function hashFile(file) {
  const userAgent = typeof navigator === 'undefined' ? '' : navigator.userAgent
  const isWebKit = /AppleWebKit/i.test(userAgent) && !/(Chrome|Chromium|Edg)/i.test(userAgent)
  if (typeof Worker === 'undefined' || isWebKit) return hashFileFallback(file)
  try {
    return await new Promise((resolve, reject) => {
      const worker = new Worker(new URL('../workers/hash-worker.js', import.meta.url), { type: 'module' })
      const timeout = setTimeout(() => { worker.terminate(); reject(new Error('asset_hash_worker_timeout')) }, 15_000)
      worker.onmessage = event => {
        clearTimeout(timeout)
        worker.terminate()
        event.data?.error ? reject(new Error(event.data.error)) : resolve(event.data.hash)
      }
      worker.onerror = event => {
        clearTimeout(timeout)
        worker.terminate()
        reject(event.error || new Error(event.message || 'asset_hash_failed'))
      }
      worker.postMessage({ file })
    })
  } catch {
    // Safari/WebKit releases vary in module Worker support. The fallback uses
    // the same incremental SHA-256 implementation and therefore produces the
    // exact same content-addressed asset URI.
    return hashFileFallback(file)
  }
}

async function uploadSingle(file, hash, request, signal) {
  const signed = await request('/sync/asset/presign-put', {
    method: 'POST', signal, body: JSON.stringify({ hash, bytes: file.size, contentType: file.type || 'application/octet-stream' }),
  })
  const response = await fetch(signed.url, { method: 'PUT', headers: { 'Content-Type': file.type || 'application/octet-stream' }, body: file, signal })
  if (!response.ok) throw new Error(`asset_put_${response.status}`)
  await request('/sync/asset/commit', {
    method: 'POST', signal, body: JSON.stringify({ hash, bytes: file.size, mime: file.type || 'application/octet-stream' }),
  })
}

async function uploadMultipart(file, hash, request, onSession, signal) {
  const start = await request('/sync/asset/multipart/start', {
    method: 'POST', signal, body: JSON.stringify({ hash, bytes: file.size, mime: file.type || 'application/octet-stream', partSize: PART_SIZE }),
  })
  await onSession(start)
  const count = Math.ceil(file.size / start.partSize)
  const numbers = Array.from({ length: count }, (_, index) => index + 1)
  const signed = await request('/sync/asset/multipart/parts', {
    method: 'POST', signal, body: JSON.stringify({ hash, uploadId: start.uploadId, partNumbers: numbers }),
  })
  const completed = []
  let cursor = 0
  async function worker() {
    while (cursor < numbers.length) {
      const partNumber = numbers[cursor++]
      const begin = (partNumber - 1) * start.partSize
      const response = await fetch(signed.parts[String(partNumber)], { method: 'PUT', body: file.slice(begin, begin + start.partSize), signal })
      if (!response.ok) throw new Error(`multipart_part_${response.status}`)
      completed.push({ partNumber, etag: response.headers.get('etag') })
    }
  }
  await Promise.all(Array.from({ length: Math.min(4, count) }, worker))
  await request('/sync/asset/multipart/complete', {
    method: 'POST', signal, body: JSON.stringify({ hash, uploadId: start.uploadId, parts: completed.sort((a, b) => a.partNumber - b.partNumber) }),
  })
}

export class AssetMap extends EventTarget {
  #entries = new Map()
  #pending = new Map()
  #localFiles = new Map()
  #controllers = new Map()
  #uncommitted = new Set()
  #posters = new Map()
  #posterPending = new Map()

  constructor(options = {}) {
    super()
    this.request = options.request || gatewayRequest
    this.read = options.read || readStore
    this.write = options.write || writeStore
    this.hash = options.hash || hashFile
  }

  async #restoreLocal(hash, ext) {
    if (this.#localFiles.has(hash)) return this.#localFiles.get(hash)
    const upload = await this.read('asset_uploads', hash)
    const blob = upload?.localBlob
    if (!(blob instanceof Blob)) return null
    const file = typeof File === 'function'
      ? new File([blob], upload.name || `asset.${upload.ext || ext}`, { type: upload.mime || blob.type })
      : blob
    this.#localFiles.set(hash, file)
    this.#uncommitted.add(hash)
    const previous = this.#entries.get(hash)
    if (!previous?.local) {
      this.#entries.set(hash, { hash, ext: upload.ext || ext, url: URL.createObjectURL(file), expiresAt: null, local: true })
    }
    return file
  }

  #trackUpload(file, hash, ext, existing, staged = false) {
    const current = this.#pending.get(hash)
    if (current) return current
    const task = this.#upload(file, hash, ext, existing, staged)
    this.#pending.set(hash, task)
    // Imports are intentionally local-first. Keep a handled promise here so a
    // missing JWT or temporary network failure cannot become an unhandled
    // rejection or remove the item that is already visible on the Canvas.
    void task.catch(() => {}).finally(() => {
      if (this.#pending.get(hash) === task) this.#pending.delete(hash)
    })
    return task
  }

  async prime(document) {
    const assets = scanAssetUris(document)
    if (!assets.length) return
    const unresolved = []
    for (const asset of assets) {
      if (this.#entries.has(asset.hash) || await this.#restoreLocal(asset.hash, asset.ext)) continue
      unresolved.push(asset)
    }
    for (let offset = 0; offset < unresolved.length; offset += 200) {
      const batch = unresolved.slice(offset, offset + 200)
      try {
        const result = await this.request('/sync/asset/resolve', {
          method: 'POST', body: JSON.stringify({ assets: batch.map(({ hash, ext }) => ({ hash, ext })) }),
        })
        for (const item of result.assets || []) this.#entries.set(item.hash, item)
      } catch {
        // A local working copy remains openable while signed out or offline.
        // Missing remote assets render as unavailable until retry succeeds.
      }
    }
    // Poster extraction is presentation work, not a prerequisite for opening
    // the document. Keep it in the background so a remote video cannot hold
    // the whole Canvas behind the loading overlay.
    for (const asset of assets) {
      if (VIDEO_EXTENSIONS.has(asset.ext)) void this.poster(asset.uri)
    }
  }

  resolve(uri) {
    const match = ASSET_URI_RE.exec(String(uri || ''))
    if (!match) return uri || null
    const entry = this.#entries.get(match[1])
    if (!entry) return null
    if (entry.expiresAt && entry.expiresAt - Date.now() < URL_REFRESH_SKEW && !this.#pending.has(match[1])) void this.retry(uri)
    return entry.url
  }

  resolvePoster(uri) {
    const match = ASSET_URI_RE.exec(String(uri || ''))
    return match ? this.#posters.get(match[1])?.url || null : null
  }

  async poster(uri) {
    const match = ASSET_URI_RE.exec(String(uri || ''))
    if (!match || !VIDEO_EXTENSIONS.has(match[2].toLowerCase())) return null
    const existing = this.#posters.get(match[1])
    if (existing) return existing
    if (!this.#posterPending.has(match[1])) {
      const task = (async () => {
        const source = this.resolve(uri)
        if (!source) return null
        const poster = await createVideoPoster(source)
        if (poster) this.#posters.set(match[1], poster)
        return poster
      })()
      this.#posterPending.set(match[1], task)
      void task.finally(() => this.#posterPending.delete(match[1]))
    }
    return this.#posterPending.get(match[1])
  }

  async retry(uri) {
    const match = ASSET_URI_RE.exec(String(uri || ''))
    if (!match) return null
    if (!this.#pending.has(match[1])) {
      const file = this.#localFiles.get(match[1]) || await this.#restoreLocal(match[1], match[2].toLowerCase())
      if (file) this.#trackUpload(file, match[1], match[2].toLowerCase())
      else {
        const task = this.prime({ uri })
        this.#pending.set(match[1], task)
        void task.catch(() => {}).finally(() => {
          if (this.#pending.get(match[1]) === task) this.#pending.delete(match[1])
        })
      }
    }
    await this.#pending.get(match[1])
    return this.resolve(uri)
  }

  async importFile(file) {
    const hash = await this.hash(file)
    const ext = extensionFor(file)
    const uri = `asset://${hash}.${ext}`
    const objectUrl = URL.createObjectURL(file)
    this.#localFiles.set(hash, file)
    this.#entries.set(hash, { hash, ext, url: objectUrl, expiresAt: null, local: true })
    this.#uncommitted.add(hash)
    const staged = {
      hash, ext, name: file.name, bytes: file.size, mime: file.type,
      status: 'uploading', localBlob: file.size <= SMALL_UPLOAD_LIMIT ? file.slice(0, file.size, file.type) : undefined,
      updatedAt: Date.now(),
    }
    // WebKit versions differ in how reliably they clone File objects into
    // IndexedDB, so persist a plain Blob and never let cache persistence block
    // the live Canvas import.
    try { await this.write('asset_uploads', staged) } catch {}
    this.#notifyUpload(hash, 'uploading', { uri })
    this.#trackUpload(file, hash, ext, { uri, objectUrl }, true)
    if (VIDEO_EXTENSIONS.has(ext)) void this.poster(uri)
    return { uri, hash, ext, displayUrl: objectUrl, committed: false }
  }

  async #upload(file, hash, ext, existing = {}, staged = false) {
    const uri = existing.uri || `asset://${hash}.${ext}`
    const objectUrl = existing.objectUrl || this.#entries.get(hash)?.url
    const controller = new AbortController()
    this.#controllers.set(hash, controller)
    if (!staged) {
      try {
        await this.write('asset_uploads', {
          hash, ext, name: file.name, bytes: file.size, mime: file.type,
          status: 'uploading', localBlob: file.size <= SMALL_UPLOAD_LIMIT ? file.slice(0, file.size, file.type) : undefined,
          updatedAt: Date.now(),
        })
      } catch {}
    }
    try {
      if (file.size <= SMALL_UPLOAD_LIMIT) await uploadSingle(file, hash, this.request, controller.signal)
      else await uploadMultipart(file, hash, this.request, session => this.write('asset_uploads', { hash, ext, name: file.name, bytes: file.size, mime: file.type, status: 'uploading', session, updatedAt: Date.now() }), controller.signal)
      try {
        await this.write('asset_uploads', { hash, ext, name: file.name, bytes: file.size, mime: file.type, status: 'committed', updatedAt: Date.now() })
      } catch {}
      this.#localFiles.delete(hash)
      this.#uncommitted.delete(hash)
      this.#notifyUpload(hash, 'committed', { uri })
      return { uri, hash, ext, displayUrl: objectUrl, committed: true }
    } catch (error) {
      const status = controller.signal.aborted ? 'cancelled' : 'failed'
      let previous = null
      try { previous = await this.read('asset_uploads', hash) } catch {}
      try {
        await this.write('asset_uploads', { ...previous, hash, ext, name: file.name, bytes: file.size, mime: file.type, status, error: String(error?.message || error), updatedAt: Date.now() })
      } catch {}
      this.#notifyUpload(hash, status, { uri, error })
      throw error
    } finally {
      this.#controllers.delete(hash)
    }
  }

  async cancel(uri) {
    const match = ASSET_URI_RE.exec(String(uri || ''))
    if (!match) return false
    this.#controllers.get(match[1])?.abort()
    const upload = await this.read('asset_uploads', match[1])
    if (upload?.session?.uploadId) {
      await this.request('/sync/asset/multipart/abort', { method: 'DELETE', body: JSON.stringify({ hash: match[1], uploadId: upload.session.uploadId }) }).catch(() => {})
    }
    return true
  }

  async allCommitted(document) {
    return (await this.commitState(document)) === 'committed'
  }

  async commitState(document) {
    let state = 'committed'
    for (const { hash } of scanAssetUris(document)) {
      let upload = null
      try { upload = await this.read('asset_uploads', hash) } catch { return 'uploading' }
      if (upload?.status === 'failed' || upload?.status === 'cancelled') return upload.status
      if (this.#uncommitted.has(hash) || (upload && upload.status !== 'committed')) state = 'uploading'
    }
    return state
  }

  async download(uri, filename) {
    const url = this.resolve(uri) || await this.retry(uri)
    if (!url) throw new Error('asset_missing')
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = filename || uri.slice(8)
    anchor.rel = 'noopener'
    anchor.click()
  }

  #notifyUpload(hash, status, extra = {}) {
    this.dispatchEvent(new CustomEvent('upload-status', { detail: { hash, status, ...extra } }))
  }

  releaseAll() {
    for (const entry of this.#entries.values()) if (entry.local && entry.url?.startsWith('blob:')) URL.revokeObjectURL(entry.url)
    this.#entries.clear()
    this.#uncommitted.clear()
    for (const poster of this.#posters.values()) if (poster.url?.startsWith('blob:')) URL.revokeObjectURL(poster.url)
    this.#posters.clear()
    this.#posterPending.clear()
    for (const controller of this.#controllers.values()) controller.abort()
    this.#controllers.clear()
  }
}
